// The things lab's ground (`?scene=things`, gate C5): a 48 m walled pen of gently rolling heightfield, a wall across it
// at z = -12 with one narrow pass (1.8 m) whose centre is the corner of four 12 m nav tiles, so the crate standing in
// it (placement `crate-pass`) touches all four, and a short wall at x = 14 with a doorway (placement `door-1`). The
// things themselves (the patch, the row of pots and crates, the sign, the door, the levers) come from the `things`
// table and are built by the module (module.ts). The navmesh is built from the same colliders (the valley's
// `navShapesOf`), the standing crates and the closed door as dynamic shapes.
import type { BodySpec } from "@rpg/collision";
import { buildNavWorld, navGeometry, type NavShape, type NavWorld } from "@rpg/nav";
import { navShapesOf } from "../valley/fight";
import type { Course } from "../valley/course";

/** The pen's half size and wall line. */
export const LAB_HALF = 24;
const WALL = 23.5;
const CELLS = 96;
/** The wall with the pass: along x at z = PASS.z; the pass spans x ±PASS.half. */
export const PASS = { z: -12, half: 0.9, height: 2, thick: 0.3 };
/** The short wall with the doorway: along z at x = DOORWAY.x, the doorway z ±DOORWAY.half round DOORWAY.z. */
export const DOORWAY = { x: 14, z: 4, half: 0.6, from: -1, to: 9, height: 2.2, thick: 0.15 };
/** Where the hero starts (facing -Z: the row of breakables and, beyond, the pass) and where the stub mount stands. */
export const LAB_SPAWN = { x: 0, z: 8, yaw: Math.PI };
export const LAB_MOUNT = { x: -20, z: 20, yaw: Math.PI };
/** The crowd walker: from the north side to beyond the pass (the nav check). */
export const WALKER = { id: 900, from: [0, -20] as [number, number], to: [0, -5] as [number, number] };
/** The navmesh: the valley's agent, 12 m tiles (48 voxels of 0.25 m). */
export const LAB_NAV = { cellSize: 0.25, cellHeight: 0.1, tileSize: 48, agentRadius: 0.4, agentHeight: 1.6, agentClimb: 0.35, maxSlope: 40, bounds: [[-LAB_HALF, -2, -LAB_HALF], [LAB_HALF, 6, LAB_HALF]] as [[number, number, number], [number, number, number]] };

/** The ground's height at (x, z): flat round the start, a gentle roll toward the walls. */
export function labHeight(x: number, z: number): number {
  const r = Math.max(Math.abs(x), Math.abs(z));
  const roll = Math.min(1, Math.max(0, (r - 6) / 10));
  return 0.18 * roll * Math.sin(x * 0.35) * Math.cos(z * 0.3);
}

/** The lab's static course: terrain, the pen's walls, the wall with the pass, the short wall with the doorway. */
export function labCourse(): Course {
  const bodies: BodySpec[] = [];
  const heights: number[] = [];
  for (let iz = 0; iz <= CELLS; iz++) for (let ix = 0; ix <= CELLS; ix++) heights.push(labHeight((ix / CELLS - 0.5) * 2 * LAB_HALF, (iz / CELLS - 0.5) * 2 * LAB_HALF));
  bodies.push({ id: "terrain", kind: "fixed", colliders: [{ shape: { type: "heightfield", rows: CELLS, cols: CELLS, heights, size: { x: 2 * LAB_HALF, y: 1, z: 2 * LAB_HALF } }, layer: "terrain", tag: "grass" }] });
  const box = (id: string, x: number, y: number, z: number, hx: number, hy: number, hz: number, tag = "stone"): void => {
    bodies.push({ id, kind: "fixed", position: { x, y, z }, colliders: [{ shape: { type: "cuboid", halfExtents: { x: hx, y: hy, z: hz } }, layer: "solid", tag }] });
  };
  for (const [id, x, z, hx, hz] of [
    ["wall-n", 0, -WALL, WALL, 0.4],
    ["wall-s", 0, WALL, WALL, 0.4],
    ["wall-w", -WALL, 0, 0.4, WALL],
    ["wall-e", WALL, 0, 0.4, WALL],
  ] as const)
    box(id, x, 1.5, z, hx, 1.5, hz);
  // the wall with the pass: two runs either side of x ±PASS.half
  const run = (WALL - PASS.half) / 2;
  box("pass-wall-w", -(PASS.half + run), PASS.height / 2, PASS.z, run, PASS.height / 2, PASS.thick);
  box("pass-wall-e", PASS.half + run, PASS.height / 2, PASS.z, run, PASS.height / 2, PASS.thick);
  // the short wall with the doorway
  const a = (DOORWAY.z - DOORWAY.half - DOORWAY.from) / 2;
  const b = (DOORWAY.to - DOORWAY.z - DOORWAY.half) / 2;
  box("door-wall-s", DOORWAY.x, DOORWAY.height / 2, DOORWAY.from + a, DOORWAY.thick, DOORWAY.height / 2, a, "wood");
  box("door-wall-n", DOORWAY.x, DOORWAY.height / 2, DOORWAY.to - b, DOORWAY.thick, DOORWAY.height / 2, b, "wood");
  return { bodies, water: [], steps: [], ramps: [], stepLaneZ: 0 };
}

/** The lab's navmesh: the course's static shapes, plus the dynamic ones (standing crates, the closed door) by id. */
export function labNav(course: Course, dynamic: Record<string, NavShape[]>): NavWorld {
  return buildNavWorld(navGeometry(navShapesOf(course.bodies)), LAB_NAV, { dynamic });
}
